import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Mail,
  Send,
  FileText,
  Euro,
  Upload,
  X,
  Paperclip,
  Globe,
  Loader2,
} from "lucide-react";
import api from "../lib/api";

const TEMPLATE_TYPES = [
  { value: "presentation", label: "Presentazione Aziendale", icon: Send, color: "text-blue-500" },
  { value: "catalog", label: "Catalogo Prodotti", icon: FileText, color: "text-purple-500" },
  { value: "pricelist", label: "Listino Prezzi", icon: Euro, color: "text-green-500" },
  { value: "other", label: "Altro", icon: Mail, color: "text-gray-500" },
];

const emptyTemplate = {
  name: "",
  type: "presentation",
  subject_it: "",
  subject_en: "",
  content_it: "",
  content_en: "",
  attachments: [],
};

export default function EmailTemplatesPage() {
  const { isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState(emptyTemplate);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("it"); // "it" or "en"

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get("/email-templates");
      setTemplates(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei template");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setFormData(emptyTemplate);
    setActiveTab("it");
    setDialogOpen(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      type: template.type,
      subject_it: template.subject_it || "",
      subject_en: template.subject_en || "",
      content_it: template.content_it || "",
      content_en: template.content_en || "",
      attachments: template.attachments || [],
    });
    setActiveTab("it");
    setDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Eliminare questo template?")) return;
    
    try {
      await api.delete(`/email-templates/${templateId}`);
      toast.success("Template eliminato");
      fetchTemplates();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedFiles = [];
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        
        const response = await api.post("/email-templates/upload-attachment", formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        
        uploadedFiles.push({
          filename: response.data.filename,
          original_name: response.data.original_name,
          url: response.data.url,
        });
      }
      
      setFormData({
        ...formData,
        attachments: [...formData.attachments, ...uploadedFiles],
      });
      toast.success(`${uploadedFiles.length} file caricati`);
    } catch (error) {
      toast.error("Errore nel caricamento dei file");
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setFormData({
      ...formData,
      attachments: formData.attachments.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Inserisci un nome per il template");
      return;
    }
    if (!formData.subject_it.trim() && !formData.subject_en.trim()) {
      toast.error("Inserisci almeno un oggetto (IT o EN)");
      return;
    }
    if (!formData.content_it.trim() && !formData.content_en.trim()) {
      toast.error("Inserisci almeno un contenuto (IT o EN)");
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/email-templates/${editingTemplate.id}`, formData);
        toast.success("Template aggiornato");
      } else {
        await api.post("/email-templates", formData);
        toast.success("Template creato");
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const getTypeInfo = (type) => {
    return TEMPLATE_TYPES.find(t => t.value === type) || TEMPLATE_TYPES[3];
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Accesso riservato agli amministratori</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6" />
            Template Email
          </h1>
          <p className="text-muted-foreground">
            Gestisci i template per le comunicazioni con i lead
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Template
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-100">Template Multilingua</p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Puoi creare template in italiano e inglese. Quando invii un'email, potrai scegliere quale lingua utilizzare.
                Usa i placeholder come <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{"{{company_name}}"}</code>, 
                <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{"{{contact_name}}"}</code> per personalizzare automaticamente il messaggio.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates List */}
      <Card>
        <CardHeader>
          <CardTitle>Template Disponibili</CardTitle>
          <CardDescription>
            {templates.length} template configurati
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8">
              <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Nessun template creato</p>
              <Button className="mt-4" onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Crea il primo template
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Oggetto (IT)</TableHead>
                  <TableHead>Allegati</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => {
                  const typeInfo = getTypeInfo(template.type);
                  const TypeIcon = typeInfo.icon;
                  return (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                          <span className="text-sm">{typeInfo.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {template.subject_it || <span className="text-muted-foreground">-</span>}
                      </TableCell>
                      <TableCell>
                        {template.attachments && template.attachments.length > 0 ? (
                          <span className="inline-flex items-center gap-1 text-sm">
                            <Paperclip className="w-3 h-3" />
                            {template.attachments.length}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(template)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(template.id)}
                            className="text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Modifica Template" : "Nuovo Template"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Template *</Label>
                <Input
                  placeholder="Es: Presentazione Azienda"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Language Tabs */}
            <div className="border rounded-lg">
              <div className="flex border-b">
                <button
                  className={`flex-1 py-3 text-center font-medium transition-colors ${
                    activeTab === "it"
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => setActiveTab("it")}
                >
                  <span className="mr-2">🇮🇹</span> Italiano
                </button>
                <button
                  className={`flex-1 py-3 text-center font-medium transition-colors ${
                    activeTab === "en"
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-muted"
                  }`}
                  onClick={() => setActiveTab("en")}
                >
                  <span className="mr-2">🇬🇧</span> English
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                {activeTab === "it" ? (
                  <>
                    <div className="space-y-2">
                      <Label>Oggetto Email (IT)</Label>
                      <Input
                        placeholder="Presentazione Lacinio - {{company_name}}"
                        value={formData.subject_it}
                        onChange={(e) => setFormData({ ...formData, subject_it: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Contenuto Email (IT)</Label>
                      <Textarea
                        rows={10}
                        placeholder="Gentile {{contact_name}},&#10;&#10;Vi contatto per presentare la nostra azienda..."
                        value={formData.content_it}
                        onChange={(e) => setFormData({ ...formData, content_it: e.target.value })}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Email Subject (EN)</Label>
                      <Input
                        placeholder="Introduction Lacinio - {{company_name}}"
                        value={formData.subject_en}
                        onChange={(e) => setFormData({ ...formData, subject_en: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Content (EN)</Label>
                      <Textarea
                        rows={10}
                        placeholder="Dear {{contact_name}},&#10;&#10;I am writing to introduce our company..."
                        value={formData.content_en}
                        onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div className="space-y-3">
              <Label>Allegati</Label>
              <div className="border-2 border-dashed rounded-lg p-4">
                {formData.attachments.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {formData.attachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-muted rounded"
                      >
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{file.original_name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => removeAttachment(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <label className="flex flex-col items-center justify-center cursor-pointer py-4">
                  {uploading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">
                        Clicca per caricare allegati (PDF, immagini, documenti)
                      </span>
                    </>
                  )}
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>

            {/* Placeholder Help */}
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">Placeholder disponibili:</p>
              <div className="flex flex-wrap gap-2">
                {["{{company_name}}", "{{contact_name}}", "{{email}}", "{{phone}}"].map((ph) => (
                  <code key={ph} className="text-xs bg-background px-2 py-1 rounded">
                    {ph}
                  </code>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  "Salva Template"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
